import React from 'react';
import { useSelector } from 'react-redux';
import {
  Ad,
  AppContent,
  baseUnit,
  ErrorBoundary,
  MEDIA_BREAKPOINTS,
 OneHCThreeVC,
 SectionWithTitle,
 SectionHeader,
 SecondarySectionTitle,
 ThreeVC,
 ThreeVCExpandable,
  ScrollableLinkImages, Section } from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import {
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getSectionArticlesByKey,
  getHreflangData,
  getSectionTitleByKey,
  getSectionImageLinks,
} from '../../store/template/homePage/homePageCusco/homePageCusco.selectors';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';


interface HomePageCuscoProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const section1Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION1)(state) || [];
  const section3Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION3)(state) || [];
  const section4Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION4)(state) || [];
  return [
    ...section1Articles,
    ...section3Articles,
    ...section4Articles,
  ];
};


export const HomePageCuscoComponent: React.FunctionComponent<HomePageCuscoProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <HomePageCuscoContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowGap: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowGap: `${baseUnit * 1.5}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowGap: `${baseUnit * 1.5}px`,
    },
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: `${baseUnit * 0.25}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
});

const paddingFactors = { large: 1, medium: 1, small: 1 };

export const HomePageCuscoContentConnected: React.FunctionComponent = () => {
  const section1DisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.SECTION1));
  const section1Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.SECTION1));
  const section2ImageLinks = useSelector(getSectionImageLinks(SECTION_NAMES.SECTION2));
  const section3DisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.SECTION3));
  const section3Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.SECTION3));
  const section4DisplayName = useSelector(getSectionTitleByKey(SECTION_NAMES.SECTION4));
  const section4Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.SECTION4));

  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const pageImage = useSelector(getMetadataImage);
  const hreflangData = useSelector(getHreflangData) || [];
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags articles={articles} title={metaTitle} description={metaDescription} pageImage={pageImage} alternateUrls={hreflangData} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainContent" noMarginTop>
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
            <div className={css(style.sectionsWrapper)}>
              <SectionWithTitle>
                <SectionHeader moreButtonSvg={null} moreButtonText={null} paddingFactors={paddingFactors}>
                  <SecondarySectionTitle>{section1DisplayName}</SecondarySectionTitle>
                </SectionHeader>
                <OneHCThreeVC cards={section1Articles} paddingFactors={paddingFactors} />
              </SectionWithTitle>
              <Section>
                <ScrollableLinkImages linkImages={section2ImageLinks} paddingFactors={paddingFactors} />
              </Section>
              <SectionWithTitle>
                <SectionHeader moreButtonSvg={null} moreButtonText={null} paddingFactors={paddingFactors}>
                  <SecondarySectionTitle>{section3DisplayName}</SecondarySectionTitle>
                </SectionHeader>
                <ThreeVCExpandable cards={section3Articles} paddingFactors={paddingFactors} />
              </SectionWithTitle>
              <SectionWithTitle>
                <SectionHeader moreButtonSvg={null} moreButtonText={null} paddingFactors={paddingFactors}>
                  <SecondarySectionTitle>{section4DisplayName}</SecondarySectionTitle>
                </SectionHeader>
                <ThreeVC cards={section4Articles} paddingFactors={paddingFactors} />
              </SectionWithTitle>
            </div>
            <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
          </NavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
